import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

import stockPhoto from "@/assets/auth/login-stock.jpg";
import fashionPhoto from "@/assets/auth/login-fashion.jpg";
import kitchenPhoto from "@/assets/auth/login-kitchen.jpg";
import { cn } from "@/lib/utils";

const slides = [
  {
    image: stockPhoto,
    title: "Know what’s low before you run out.",
    body: "Stock across every product, with reorder alerts that arrive in time.",
    alt: "Shop owner packing an order",
  },
  {
    image: fashionPhoto,
    title: "One link for everything you sell.",
    body: "Share your sales link and let customers buy without the back-and-forth.",
    alt: "Fashion seller folding clothes",
  },
  {
    image: kitchenPhoto,
    title: "From the kitchen to a paid order.",
    body: "Products, menus, and checkout on the same page you share.",
    alt: "Baker packing pastries for an order",
  },
];

export function LoginShowcase() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (motion.matches) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, []);

  const slide = slides[index];

  return (
    <aside className="hidden h-full p-3 lg:block lg:w-[46%] lg:py-4 lg:pr-4 lg:pl-0">
      <div className="relative h-full overflow-hidden rounded-[1.75rem] bg-[#1a1523]">
        {slides.map((item, itemIndex) => (
          <img
            key={item.alt}
            src={item.image}
            alt={itemIndex === index ? item.alt : ""}
            className={cn(
              "absolute inset-0 h-full w-full object-cover transition-opacity duration-700",
              itemIndex === index ? "opacity-100" : "opacity-0",
            )}
          />
        ))}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/25"
          aria-hidden
        />

        <Link
          to="/"
          aria-label="Go to homepage"
          className="absolute top-4 right-4 z-20 inline-flex size-9 items-center justify-center rounded-full bg-white/90 text-[#292d32] transition-colors hover:bg-white"
        >
          <X className="size-4" />
        </Link>

        <div className="pointer-events-none relative flex h-full flex-col justify-between p-8 text-white xl:p-10">
          <div className="max-w-md pr-12">
            <h2 className="font-display text-4xl leading-[1.05] text-white xl:text-5xl">
              {slide.title}
            </h2>
            <p className="mt-4 max-w-sm text-sm font-medium leading-relaxed text-white/85">
              {slide.body}
            </p>
            <div className="pointer-events-auto mt-6 flex items-center gap-1.5" role="tablist" aria-label="Stories">
              {slides.map((item, itemIndex) => (
                <button
                  key={item.alt}
                  type="button"
                  role="tab"
                  aria-selected={itemIndex === index}
                  aria-label={item.title}
                  onClick={() => setIndex(itemIndex)}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    itemIndex === index ? "w-6 bg-white" : "w-1.5 bg-white/55 hover:bg-white/80",
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
